// ProcessTimeline.js
import React from 'react';
import { motion } from 'framer-motion';
import { Clock } from 'lucide-react';

const ProcessTimeline = () => {
  const timelines = [
    {
      pathway: 'Express Entry',
      stages: [
        { step: 'Build your Express Entry profile', duration: '1-2 weeks', description: 'Fill in the online profile with the information and documents it asks for' },
        { step: 'Invitation to Apply (ITA)', duration: 'Varies', description: 'Draws normally fall 2-4 weeks apart, and whether you are picked rests on your CRS score' },
        { step: 'File the full application', duration: '60 days', description: 'The ITA gives you 60 days to pull everything together and submit it' },
        { step: 'IRCC assessment', duration: '6 months', description: 'IRCC verifies your evidence and completes its background checks' },
        { step: 'Confirmation and visa issued', duration: '1-2 weeks', description: 'Your Confirmation of Permanent Residence (COPR) and PR visa come through' }
      ],
      totalTime: '6-8 months',
      color: 'from-blue-500 to-blue-600'
    },
    {
      pathway: 'Provincial Nominee Program',
      stages: [
        { step: 'Apply to the provincial stream', duration: '2-4 months', description: 'Lodge your application with the province you have chosen' },
        { step: 'Nomination decision', duration: 'Varies by province', description: 'The province assesses the case and, where successful, issues a nomination certificate' },
        { step: 'File the federal application', duration: '1-2 weeks', description: 'Send in your PR application, on paper or through Express Entry, with the certificate attached' },
        { step: 'Federal stage', duration: '15-19 months', description: 'IRCC works through the federal half of the application' },
        { step: 'Confirmation and visa issued', duration: '1-2 weeks', description: 'COPR and PR visa are released' }
      ],
      totalTime: '18-24 months',
      color: 'from-green-500 to-green-600'
    },
    {
      pathway: 'Family Sponsorship (Spouse)',
      stages: [
        { step: 'Check that the sponsor qualifies', duration: '1-2 weeks', description: 'Confirm the sponsor meets every requirement before anything is filed' },
        { step: 'Assemble and lodge the package', duration: '4-6 weeks', description: 'Collect the evidence and submit the sponsorship application in full' },
        { step: 'Under assessment', duration: '12 months', description: 'IRCC handles the sponsorship and the PR application as one file' },
        { step: 'Interview, where one is called', duration: 'Varies', description: 'Attend only if IRCC asks for it' },
        { step: 'Confirmation and visa issued', duration: '2-4 weeks', description: 'COPR and PR visa are released' }
      ],
      totalTime: '12-14 months',
      color: 'from-purple-500 to-purple-600'
    }
  ];

  return (
    <section className="py-16 bg-surface">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-semibold text-primary mb-4">
            How Long Each Route Takes
          </h2>
          <p className="text-gray-600 text-base font-light max-w-3xl mx-auto">
            Knowing roughly how long a route runs makes it far easier to plan the rest of your move around it.
          </p>
        </motion.div>

        {/* Timelines */}
        <div className="space-y-12">
          {timelines.map((timeline, tIndex) => (
            <motion.div
              key={tIndex}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: tIndex * 0.2 }}
              className="bg-white rounded-lg shadow-sm p-6 md:p-8"
            >
              {/* Timeline Header */}
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h3 className="text-xl font-semibold text-primary mb-2">
                    {timeline.pathway}
                  </h3>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Clock size={16} />
                    <span className="font-light">Typical time, end to end: <strong>{timeline.totalTime}</strong></span>
                  </div>
                </div>
              </div>

              {/* Timeline Steps */}
              <div className="relative">
                {/* Vertical Line */}
                <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>

                {/* Steps */}
                <div className="space-y-8">
                  {timeline.stages.map((stage, sIndex) => (
                    <motion.div
                      key={sIndex}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: sIndex * 0.1 }}
                      className="relative pl-12"
                    >
                      {/* Step Number */}
                      <div className={`absolute left-0 w-8 h-8 rounded-full bg-gradient-to-br ${timeline.color} flex items-center justify-center text-white text-sm font-semibold`}>
                        {sIndex + 1}
                      </div>

                      {/* Step Content */}
                      <div className="bg-surface p-4 rounded-lg">
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="text-base font-semibold text-primary">
                            {stage.step}
                          </h4>
                          <span className="text-xs font-light text-secondary bg-white px-3 py-1 rounded-full whitespace-nowrap ml-4">
                            {stage.duration}
                          </span>
                        </div>
                        <p className="text-gray-600 text-sm font-light leading-relaxed">
                          {stage.description}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Important Notes */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-12 grid md:grid-cols-2 gap-6"
        >
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <h4 className="text-base font-semibold text-primary mb-3">
              What Moves the Timeline
            </h4>
            <ul className="space-y-2 text-sm text-gray-700 font-light">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>How complete the application and its evidence are when filed</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>Your country of origin and how involved the background checks become</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>The volume IRCC is carrying at the time and its capacity to clear it</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>Whether further verification or an interview is called for</span>
              </li>
            </ul>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h4 className="text-base font-semibold text-primary mb-3">
              Where We Come In
            </h4>
            <ul className="space-y-2 text-sm text-gray-700 font-light">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>Checking the file is complete before it goes in, so it does not stall</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>Preparing each document to the standard IRCC expects</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>Watching the status and turning around any request quickly</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>Staying available for questions from start to finish</span>
              </li>
            </ul>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ProcessTimeline;
